/**
 * Push-permission ledger
 * -----------------------
 * Records WHEN and HOW we surfaced the notification-permission ask, plus the
 * outcome, so the onboarding flow can:
 *   1. Ask once on the tour ("Let Coach Cedar reach me" → fires the OS dialog).
 *   2. NOT re-fire the iOS one-shot on someone who tapped "No, Thanks" (a soft
 *      decline never touches the OS dialog) — instead re-ask once right after
 *      the personality "ready" moment, where the native prompt is still
 *      available.
 *
 * Why a ledger and not the old boolean: the legacy `notification_permission_asked`
 * key was written ONLY on grant, so it could not represent "declined" or carry a
 * timestamp. The OS status (Notifications.getPermissionsAsync) stays the ground
 * truth for "granted"; this ledger's job is tracking declines + which surface
 * asked + when, so Phase 2 re-asks can be timed and the iOS one-shot is never
 * wasted.
 *
 * Storage is lazy-imported inside the async functions so the pure helpers below
 * stay unit-testable under the node jest environment (services/storage.ts pulls
 * in react-native, which cannot load in plain node).
 */

export type PushPermissionOutcome = 'never' | 'granted' | 'soft_declined' | 'os_denied';
export type PushPermissionSurface = 'tour' | 'post_personality' | 'paywall' | 'launch' | 'settings' | 'post_first_plan';
export type OsPermissionStatus = 'granted' | 'denied' | 'undetermined';

export interface PushPermissionLedger {
  /** never = not yet surfaced; soft_declined = tapped "No, Thanks" (OS never prompted); os_denied = tapped Don't Allow in the system dialog. */
  outcome: PushPermissionOutcome;
  /** epoch ms of the last time we surfaced the ask (null = never). */
  lastAskedAt: number | null;
  askCount: number;
  lastSurface: PushPermissionSurface | null;
  /** epoch ms when the ~48h-post-first-plan re-ask was surfaced (null = not yet).
   *  Distinct from `outcome`: the deniers→Settings branch can't move the OS
   *  outcome, so this is what guards the post-plan re-ask from re-firing on
   *  every launch. */
  postPlanReaskAt: number | null;
  /** The outcome we have successfully reported to the server (null = none).
   *  Phase 3: the outcome used to live ONLY here and reached the server in no
   *  form, so notification opt-in could not be measured at all. This is what
   *  stops the launch reconciler re-emitting an outcome that already landed. */
  lastSyncedOutcome: PushPermissionOutcome | null;
}

export const PUSH_PERMISSION_LEDGER_KEY = 'push_permission_ledger';
export const LEGACY_PERMISSION_ASKED_KEY = 'notification_permission_asked';

export const DEFAULT_LEDGER: PushPermissionLedger = {
  outcome: 'never',
  lastAskedAt: null,
  askCount: 0,
  lastSurface: null,
  postPlanReaskAt: null,
  lastSyncedOutcome: null,
};

const VALID_OUTCOMES: PushPermissionOutcome[] = [
  'never',
  'granted',
  'soft_declined',
  'os_denied',
];

/**
 * Parse the stored ledger, migrating the legacy boolean. Never throws — a
 * corrupt/absent value falls back to DEFAULT (never asked).
 */
export function ledgerFromStored(
  raw: string | null,
  legacyAsked: string | null,
): PushPermissionLedger {
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed === 'object' &&
        VALID_OUTCOMES.includes(parsed.outcome)
      ) {
        return {
          outcome: parsed.outcome,
          lastAskedAt: typeof parsed.lastAskedAt === 'number' ? parsed.lastAskedAt : null,
          askCount: typeof parsed.askCount === 'number' ? parsed.askCount : 0,
          lastSurface: parsed.lastSurface ?? null,
          postPlanReaskAt: typeof parsed.postPlanReaskAt === 'number' ? parsed.postPlanReaskAt : null,
          // Validated, never trusted verbatim: a corrupt value here would read
          // as "already synced" and permanently suppress emission for this user.
          lastSyncedOutcome: VALID_OUTCOMES.includes(parsed.lastSyncedOutcome)
            ? parsed.lastSyncedOutcome
            : null,
        };
      }
    } catch {
      // fall through to legacy / default
    }
  }
  // Legacy boolean was only ever written on a successful grant.
  if (legacyAsked === 'true') {
    return { ...DEFAULT_LEDGER, outcome: 'granted' };
  }
  return DEFAULT_LEDGER;
}

/** Pure transition: apply a new outcome to a ledger. */
export function applyOutcome(
  prev: PushPermissionLedger,
  outcome: PushPermissionOutcome,
  surface: PushPermissionSurface,
  nowMs: number,
): PushPermissionLedger {
  return {
    outcome,
    lastAskedAt: nowMs,
    askCount: prev.askCount + 1,
    lastSurface: surface,
    postPlanReaskAt: prev.postPlanReaskAt,
    // Carried, NOT reset. This object is rebuilt field-by-field (no spread), so
    // omitting this silently drops the sync marker on every outcome write and
    // the reconciler would re-emit forever. The NEW outcome is deliberately not
    // marked synced — that only happens once the POST succeeds.
    lastSyncedOutcome: prev.lastSyncedOutcome,
  };
}

/** Outcomes the backend allowlist accepts. 'never' is a local-only sentinel —
 *  emitting it would 400 and be dropped. */
export function shouldEmitOutcome(outcome: PushPermissionOutcome): boolean {
  return outcome === 'granted' || outcome === 'soft_declined' || outcome === 'os_denied';
}

/** Does this ledger hold an outcome the server has not seen?
 *
 *  Keyed on outcome IDENTITY, not ask count: a user who soft-declines twice
 *  syncs once. The funnel question is what they ultimately chose; askCount
 *  rides in the event context for the rest.
 *
 *  MISATTRIBUTION GUARD — `lastAskedAt !== null`. clearPushPermissionLedger
 *  (logout/401) removes PUSH_PERMISSION_LEDGER_KEY but deliberately leaves the
 *  legacy LEGACY_PERMISSION_ASKED_KEY boolean, because the ask sites still read
 *  it for device-wide suppression. So after a logout, ledgerFromStored(null,
 *  'true') migrates to `{outcome: 'granted', lastAskedAt: null, askCount: 0}` —
 *  and without this guard the next user to sign in on that device would have
 *  the PREVIOUS user's grant reconciled onto their own id, inflating the very
 *  opt-in number this instrumentation exists to measure. Auth-gating the
 *  reconciler does not help: the new user is legitimately resolved.
 *
 *  Only applyOutcome sets lastAskedAt, so it is the marker for "this install
 *  actually observed this outcome". A legacy-only ledger is pre-Phase-3
 *  history with no surface and no timestamp — correctly out of the funnel.
 *  The live path is unaffected: recordPushPermissionOutcome emits from its own
 *  pendingEmit, never through this predicate. */
export function needsOutcomeSync(ledger: PushPermissionLedger): boolean {
  if (ledger.lastAskedAt === null) return false;
  return shouldEmitOutcome(ledger.outcome) && ledger.outcome !== ledger.lastSyncedOutcome;
}

/**
 * Should we fire a REAL OS prompt at the post-personality re-ask?
 * Only when the native one-shot is still available (status 'undetermined')
 * AND the user explicitly tapped "No, Thanks" on the tour (outcome
 * 'soft_declined'). If the OS has already resolved (granted/denied) we never
 * re-fire — a denied iOS user needs a Settings deep-link (Phase 2), not another
 * no-op prompt. 'never' (skipped the tour / pre-feature users) is intentionally
 * excluded: this predicate is evaluated on every chat mount where personality
 * is 'completed', so re-asking a 'never' user there would surprise established
 * users on cold start.
 */
export function shouldFireReaskAtPersonalityReady(
  ledger: PushPermissionLedger,
  osStatus: OsPermissionStatus,
): boolean {
  if (osStatus !== 'undetermined') return false;
  return ledger.outcome === 'soft_declined';
}

/** ~48h after the user's first plan — the high-intent re-ask window. */
export const POST_FIRST_PLAN_REASK_DELAY_MS = 48 * 60 * 60 * 1000;

/**
 * Should we surface the ~48h-post-first-plan re-ask, and if so HOW?
 *   - 'os_dialog' → fire the real OS prompt (soft-decliner whose one-shot is
 *     still available).
 *   - 'settings'  → show the deniers primer whose CTA opens iOS Settings (the
 *     OS one-shot is spent, so a fresh prompt is impossible).
 *   - null        → do nothing.
 *
 * Fires AT MOST ONCE ever (guarded by `ledger.postPlanReaskAt`) and ONLY for
 * users who explicitly went through the Phase 1 ask (soft_declined / os_denied).
 * A 'never' user (pre-feature or tour-skipped) is left alone — matching the
 * post-personality re-ask's caution about surprising established users.
 * `granted` is always a no-op.
 *
 * Anchored to the DURABLE backend first-plan time (/auth/me first_plan_created_at),
 * not a frontend timestamp — so it can't drift, reset on storage-clear, or miss
 * a user who never re-observed the plan state.
 */
export function shouldFirePostFirstPlanReask(
  ledger: PushPermissionLedger,
  osStatus: OsPermissionStatus,
  firstPlanCreatedAtMs: number | null,
  nowMs: number,
): 'os_dialog' | 'settings' | null {
  if (osStatus === 'granted') return null;
  if (ledger.postPlanReaskAt !== null) return null; // already did the post-plan re-ask
  if (firstPlanCreatedAtMs === null) return null; // no plan yet
  if (nowMs - firstPlanCreatedAtMs < POST_FIRST_PLAN_REASK_DELAY_MS) return null;
  if (ledger.outcome === 'os_denied') return 'settings';
  if (ledger.outcome === 'soft_declined' && osStatus === 'undetermined') return 'os_dialog';
  return null;
}

// ── Storage-bound API (lazy import keeps the pure helpers node-testable) ──

async function getStorage() {
  const mod = await import('./storage');
  return mod.storage;
}

// Serialize all ledger read-modify-writes so two concurrent writers
// (recordPushPermissionOutcome + recordPostPlanReaskShown both mutate
// PUSH_PERMISSION_LEDGER_KEY) can't lose-update each other — there is no CAS at
// the storage layer, so we serialize at the source (P2-4). Each queued write
// re-reads inside the chain, so it sees the prior write's result.
let _ledgerWriteChain: Promise<void> = Promise.resolve();
function serializeLedgerWrite(fn: () => Promise<void>): Promise<void> {
  const run = _ledgerWriteChain.then(fn, fn);
  _ledgerWriteChain = run.catch(() => {});
  return run;
}

export async function getPushPermissionLedger(): Promise<PushPermissionLedger> {
  try {
    const storage = await getStorage();
    const [raw, legacy] = await Promise.all([
      storage.getItem(PUSH_PERMISSION_LEDGER_KEY),
      storage.getItem(LEGACY_PERMISSION_ASKED_KEY),
    ]);
    return ledgerFromStored(raw, legacy);
  } catch {
    return DEFAULT_LEDGER;
  }
}

export async function recordPushPermissionOutcome(
  outcome: PushPermissionOutcome,
  surface: PushPermissionSurface,
): Promise<void> {
  type PendingEmit = {
    outcome: PushPermissionOutcome;
    surface: PushPermissionSurface;
    askCount: number;
  };
  let pendingEmit: PendingEmit | null = null;
  await serializeLedgerWrite(async () => {
    try {
      const storage = await getStorage();
      const current = await getPushPermissionLedger();
      const next = applyOutcome(current, outcome, surface, Date.now());
      await storage.setItem(PUSH_PERMISSION_LEDGER_KEY, JSON.stringify(next));
      // Mirror the legacy `notification_permission_asked` boolean ONLY on grant —
      // this matches its original grant-only semantics, so the existing
      // post-purchase / launch ask sites (which check live OS status first, then
      // this boolean) keep suppressing correctly. Declines are deliberately NOT
      // written here; those sites read OS status for the denied case.
      if (outcome === 'granted') {
        await storage.setItem(LEGACY_PERMISSION_ASKED_KEY, 'true');
      }
      pendingEmit = { outcome, surface, askCount: next.askCount };
    } catch {
      // Non-fatal: a missed ledger write at worst re-surfaces the ask later.
    }
  });

  // Phase 3: emit AFTER the serialized write resolves, never inside it. The
  // chain is `_ledgerWriteChain.then(fn, fn)`, so a nested write queues rather
  // than deadlocking — but scheduling from out here keeps the storage write's
  // latency independent of the network.
  //
  // THIS IS THE SINGLE EMISSION CHOKE POINT. All four call sites
  // (OnboardingTour x2, ChatScreen post-first-plan, personalityReaskRunner's
  // injected recordOutcome) funnel through here, so no site can forget to emit.
  const emit = pendingEmit as PendingEmit | null;
  if (emit) void emitOutcome(emit.outcome, emit.surface, emit.askCount);
}

/**
 * Report an outcome to the server, then mark it synced so the launch
 * reconciler stops re-sending it.
 *
 * AT-MOST-ONCE, deliberately. `_record` on the backend swallows insert failures
 * and still answers 204, so a `true` here does not prove persistence — marking
 * synced on a 204 can lose an event. The alternative (never marking synced)
 * re-emits on every launch forever, which is worse. Duplicates are also
 * harmless: the read layer takes the first event per user per outcome.
 */
async function emitOutcome(
  outcome: PushPermissionOutcome,
  surface: PushPermissionSurface,
  askCount: number,
  reconciled = false,
): Promise<void> {
  if (!shouldEmitOutcome(outcome)) return;
  try {
    const { trackPushPermission } = await import('./analyticsEvents');
    const ok = await trackPushPermission(outcome as 'granted' | 'soft_declined' | 'os_denied', {
      surface,
      ask_count: askCount,
      ...(reconciled ? { reconciled: true } : {}),
    });
    // postEvent returns false on 401/403 (token-refresh window) as well as on
    // network failure — treat any falsy result as UNSYNCED so a transient auth
    // gap does not permanently mark the outcome delivered.
    if (!ok) return;
    await serializeLedgerWrite(async () => {
      try {
        const storage = await getStorage();
        const current = await getPushPermissionLedger();
        // A logout clear can land between the POST and this write; re-reading
        // inside the chain means we never resurrect a cleared ledger.
        if (current.outcome === 'never') return;
        const next: PushPermissionLedger = { ...current, lastSyncedOutcome: outcome };
        await storage.setItem(PUSH_PERMISSION_LEDGER_KEY, JSON.stringify(next));
      } catch {
        // non-fatal — the reconciler retries next launch
      }
    });
  } catch {
    // non-fatal
  }
}

/**
 * Launch/foreground reconciler — closes the offline and crash gaps.
 *
 * MUST run only after auth resolves: clearPushPermissionLedger fires on
 * logout/401, so running earlier could re-emit user A's residual outcome under
 * user B's token.
 */
export async function reconcilePushPermissionSync(): Promise<boolean> {
  try {
    const ledger = await getPushPermissionLedger();
    if (!needsOutcomeSync(ledger)) return false;
    await emitOutcome(
      ledger.outcome,
      // lastSurface is nullable and, unlike `outcome`, is NOT validated on read
      // — fall back rather than sending a value the backend may not know.
      ledger.lastSurface ?? 'launch',
      ledger.askCount,
      true,
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Mark the ~48h-post-first-plan re-ask as surfaced so it never re-fires. Used
 * for the deniers→Settings branch (which can't move the OS outcome) AND, belt-
 * and-suspenders, the os_dialog branch. Merges into the current ledger without
 * touching `outcome`.
 */
export async function recordPostPlanReaskShown(): Promise<void> {
  return serializeLedgerWrite(async () => {
    try {
      const storage = await getStorage();
      const current = await getPushPermissionLedger();
      const next: PushPermissionLedger = { ...current, postPlanReaskAt: Date.now() };
      await storage.setItem(PUSH_PERMISSION_LEDGER_KEY, JSON.stringify(next));
    } catch {
      // non-fatal
    }
  });
}

export async function clearPushPermissionLedger(): Promise<void> {
  // Route the clear through the SAME write-serialization chain as the writers
  // (#5) — otherwise a logout/401 clear can interleave with an in-flight
  // recordPushPermissionOutcome / recordPostPlanReaskShown and lose-update.
  return serializeLedgerWrite(async () => {
    try {
      const storage = await getStorage();
      await storage.removeItem(PUSH_PERMISSION_LEDGER_KEY);
    } catch {
      // non-fatal
    }
  });
}
